import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Upload, MagnifyingGlass, Scan, Bank } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BankStatements() {
  const [statements, setStatements] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [scanning, setScanning] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [transactionsRes, clientsRes] = await Promise.all([
        api.get('/bank-transactions'),
        api.get('/clients'),
      ]);
      setTransactions(transactionsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedClient) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    const uploadedStatements = [];
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        const { data } = await api.post(
          `/bank-statements/upload?client_id=${selectedClient}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        uploadedStatements.push(data);
        successCount++;
        
        // Auto-scan each uploaded statement
        try {
          await api.post(`/bank-statements/${data.id}/scan`);
        } catch (scanError) {
          toast.error(`Auto-scan failed for ${file.name}`);
        }
      } catch (error) {
        errorCount++;
      }
    }

    setStatements([...uploadedStatements, ...statements]);
    setUploadDialogOpen(false);
    setUploadProgress({ current: 0, total: 0 });
    
    // Refresh transactions after all uploads
    const transactionsRes = await api.get('/bank-transactions');
    setTransactions(transactionsRes.data);
    
    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} statement${successCount > 1 ? 's' : ''} uploaded and scanned`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else {
      toast.error('Failed to upload statements');
    }
    
    setUploading(false);
    setSelectedClient('');
    e.target.value = '';
  };

  const handleScan = async (statementId) => {
    setScanning(statementId);
    try {
      const { data } = await api.post(`/bank-statements/${statementId}/scan`);
      toast.success(data.message);
      // Refresh transactions
      const transactionsRes = await api.get('/bank-transactions');
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setScanning(null);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const filteredTransactions = transactions.filter((txn) => {
    const matchesSearch = txn.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = filterClient === 'all' || txn.client_id === filterClient;
    return matchesSearch && matchesClient;
  });

  return (
    <div className="space-y-6" data-testid="bank-statements-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Bank Statements</h1>
          <p className="text-muted-foreground">Upload bank statements and extract transactions</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-statement-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Statement
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Bank Statement</DialogTitle>
              <DialogDescription>
                Select the client and upload their bank statement. AI will extract transactions automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="statement-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Files</Label>
                <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
                  <input
                    type="file"
                    id="statement-upload"
                    accept=".pdf,.csv,.xlsx"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={!selectedClient || uploading}
                    multiple
                    data-testid="statement-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('statement-upload').click()}
                    disabled={!selectedClient || uploading}
                    data-testid="select-statement-file-btn"
                  >
                    {uploading 
                      ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` 
                      : 'Select Files'}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">PDF, CSV, or Excel • Select multiple files</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="transaction-search-input"
          />
        </div>
        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-48" data-testid="transaction-client-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map((client) => (
              <SelectItem key={client.id} value={client.id}>
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Transactions Table */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4 flex items-center gap-2">
            <Bank className="w-5 h-5" />
            Bank Transactions
          </CardTitle>
          <CardDescription>
            {filteredTransactions.length} transactions found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No transactions found</p>
              <p className="text-sm text-muted-foreground mt-1">Upload a bank statement to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((txn) => (
                  <TableRow key={txn.id} data-testid={`transaction-row-${txn.id}`}>
                    <TableCell className="font-medium">{getClientName(txn.client_id)}</TableCell>
                    <TableCell>{txn.transaction_date || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{txn.description || '-'}</TableCell>
                    <TableCell>{txn.reference || '-'}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}
                      >
                        {txn.transaction_type}
                      </Badge>
                    </TableCell>
                    <TableCell className={txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                      {txn.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={txn.status === 'matched' ? 'status-matched' : 'status-unmatched'}
                      >
                        {txn.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
